import { useEffect, useState } from 'react';
import './PreconsultIntakePanel.css';
import {
  getIntakeQuestionnaire,
  type DoctorPreconsultView,
  type PreconsultReadResult,
} from '@/dashboard/api';
import { AcBadge } from '@/dashboard/ui';
import { consultErrorMessage } from '@/dashboard/doctor/doctor-consult-view';

/**
 * PreconsultIntakePanel -- the consult page's HEADLESS questionnaire FETCH
 * OWNER (it renders nothing).
 *
 * FETCH OWNERSHIP (CONSULT-START-TRUTH-V1, W-32 -- IDLE-MEANS-IDLE; UNCHANGED
 * by the fold): this component is the page's single questionnaire fetch owner.
 * It performs the ONE read on mount, deduped IN FLIGHT per appointment at
 * module level so a StrictMode double-effect (dev) or a rapid remount can never
 * issue two concurrent GETs -- one owner, ONE GET on mount, exactly. It mirrors
 * every panel-state transition up via `onStateChange`; ConsultIntakeQuestions
 * Block and PreconsultSafetyStrip consume that lifted state as a prop and never
 * fetch. The entry clears on settle, so a later re-entry always reads fresh --
 * this is a dedupe, never a stale cache. Ratchet:
 * tests/e2e/role-acceptance/consult-start-truth.spec.ts.
 *
 * ONE FLOW (DOCTOR-INTAKE-ONE-FLOW-V1, founder ruling 2026-08-14 --
 * completing the W-30 fold doctrine "the doctor confirms the intake by DOING
 * the intake"): the review ceremony is fully RETIRED. The Mark-intake-
 * reviewed act, its #506 completeness gate, and the awaiting-review chip no
 * longer exist anywhere; the display-only reason-for-visit header line is
 * gone too -- the reason now has ONE home, the FIRST visible intake question
 * in ConsultIntakeQuestionsBlock (it IS the chief complaint; the Doctor's
 * record rail mirrors it read-only). Review responsibility lives in the
 * close act's advisory gap lines (WARN-only, the standing close ruling).
 *
 * The DOCTOR (clinical) audience yields the READY state; a non-clinical
 * (status) audience yields `unavailable` (the wire law). Passive VIEWING
 * (getIntakeQuestionnaire) writes NOTHING -- the only write is the block's
 * doctor-authored autosave.
 */

export type PanelState =
  | { kind: 'loading' }
  | { kind: 'error'; text: string }
  | { kind: 'ready'; view: DoctorPreconsultView }
  | { kind: 'unavailable' };

/**
 * THE ONE questionnaire read on the consult page (CONSULT-START-TRUTH-V1,
 * W-32 ratchet -- IDLE-MEANS-IDLE). This panel is the page's single fetch
 * owner: ConsultIntakeQuestionsBlock consumes this read as a prop and never
 * fetches. The read is deduped IN FLIGHT per appointment at module level so
 * a StrictMode double-effect (dev) or a rapid remount can never issue two
 * concurrent GETs -- one owner, ONE GET on mount, exactly (the
 * consultStartFiredIds once-guard precedent next door). The entry clears on
 * settle, so a later re-entry always reads fresh -- this is a dedupe,
 * never a stale cache. Ratchet:
 * tests/e2e/role-acceptance/consult-start-truth.spec.ts.
 */
const inflightQuestionnaireReads = new Map<
  number,
  Promise<PreconsultReadResult>
>();

function readQuestionnaireDeduped(
  appointmentId: number,
): Promise<PreconsultReadResult> {
  const inflight = inflightQuestionnaireReads.get(appointmentId);
  if (inflight !== undefined) return inflight;
  const read = getIntakeQuestionnaire(appointmentId).finally(() => {
    inflightQuestionnaireReads.delete(appointmentId);
  });
  inflightQuestionnaireReads.set(appointmentId, read);
  return read;
}

/**
 * INTAKE-REVIEW-REACHABILITY-CORRECTIVE-V1 -- map the role-shaped read to the
 * panel state. The DOCTOR (clinical) audience yields the READY state; any
 * non-clinical (status) audience yields `unavailable`. Pure + exported so the
 * ownership seam is a pinned unit, never a fetch-only behaviour that can
 * silently regress off a build.
 */
export function preconsultPanelStateFromRead(
  res: PreconsultReadResult,
): PanelState {
  return res.audience === 'clinical'
    ? { kind: 'ready', view: res }
    : { kind: 'unavailable' };
}

/* DOCTOR-INTAKE-ONE-FLOW-V1 (R1 + R3a, founder ruling 2026-08-14):
 *   - the review-act helper, the review-chip derivation, and the reviewed
 *     chip label are REMOVED with the review ceremony (the act, its #506
 *     gate, and the awaiting-review chip no longer exist);
 *   - the display-only reason-line derivation and its type are REMOVED
 *     with the header line: the reason now has ONE home -- the FIRST
 *     visible intake question (ConsultIntakeQuestionsBlock), mirrored
 *     read-only in the Doctor's record rail. */

// --- The MEDICAL-SAFETY STRIP (DOCTOR-OS-BUILD-PHASE-1-V1, brief s.7.3 C1) ---
//
// The R-MED answers in the doctor's face at the top of the consult: the
// SAFETY-FLAGGED patient-reported answers render as read-only chips beside the
// banner, provenance-labeled, composed ENTIRELY from the existing intake read
// (per-answer safetyFlag = the questionnaire's own config-flagged options --
// no hardcoded question keys, no new contract, zero prefill). Safe default S9
// (readiness #416): read-only chips, NEVER a blocking acknowledgement --
// Q-R13 stays the founder/Rajeshwari's call.
//
// R-MED honesty: an Unknown never renders as a No. The positive
// "No safety-flagged answers" chip renders ONLY when the questionnaire is
// COMPLETE with zero flags; an incomplete or absent intake renders NO strip
// claim at all (the reason-for-visit context line carries the honest state).

/** Read-only wording over the permitted saved answer. Known question keys
 * have concise staff labels; custom questions retain their configured prompt.
 * Detail text is never dropped, and meaningful option labels remain alongside it.
 */
function savedSafetyReport(answer: DoctorPreconsultView['answers'][number]): string {
  const labels = answer.selectedLabels.map(label => label.trim()).filter(Boolean);
  const detail = answer.detailText?.trim() ?? '';
  const choice = detail !== '' && labels.length === 1 && labels[0].toLowerCase() === 'yes'
    ? '' : labels.join(', ');
  const value = [choice, detail].filter(Boolean).join(' — ');
  if (answer.questionKey === 'relevant_conditions') return value || 'Condition reported';
  const label: Readonly<Record<string, string>> = {
    reason_for_visit: 'Reason for visit',
    onset: 'Started',
    pain_level: 'Pain',
    allergies: 'Allergy reported',
    medications: 'Medicines',
    previous_treatment: 'Previous treatment',
    anything_else: 'Additional notes',
  };
  return `${label[answer.questionKey] ?? answer.prompt}: ${value || 'Details not recorded'}`;
}

export function PreconsultSafetyStrip({
  state,
}: {
  state: PanelState;
}): JSX.Element | null {
  if (state.kind !== 'ready') return null;
  const view = state.view;
  if (view.underIdentityReview) return null;
  if (!view.questionnairePresent || view.answers.length === 0) return null;
  const flagged = view.answers.filter((a) => a.safetyFlag);
  if (flagged.length === 0 && !view.completion.complete) return null;
  return (
    <section
      className="consult-safety"
      aria-label="Patient-reported safety"
      data-testid="consult-safety-strip"
    >
      <span className="consult-safety__label">Patient-reported safety</span>
      {flagged.length > 0 ? (
        flagged.map((a) => (
          <AcBadge
            key={a.questionKey}
            tone="danger"
            testId={`consult-safety-flag-${a.questionKey}`}
          >
            {savedSafetyReport(a)}
          </AcBadge>
        ))
      ) : (
        <AcBadge tone="positive" testId="consult-safety-none">
          No safety-flagged answers
        </AcBadge>
      )}
    </section>
  );
}

export interface PreconsultIntakePanelProps {
  readonly appointmentId: number;
  /** State mirror (DOCTOR-OS-BUILD-PHASE-1-V1): the consult page renders the
   *  medical-safety strip, the reason-for-visit line, and the questions block
   *  from THIS panel's ONE fetch -- one read, one truth, never a second intake
   *  request. */
  readonly onStateChange?: (state: PanelState) => void;
}

/**
 * The HEADLESS questionnaire fetch owner. It fetches once on mount (deduped),
 * mirrors state up via `onStateChange`, and renders NOTHING -- the former
 * review card is folded (see the file header). Keeping it mounted preserves the
 * #514 one-fetch-owner discipline; its consumers (the questions block, the
 * safety strip, the reason-for-visit line) read the lifted state.
 */
export default function PreconsultIntakePanel({
  appointmentId,
  onStateChange,
}: PreconsultIntakePanelProps): JSX.Element | null {
  const [state, setState] = useState<PanelState>({ kind: 'loading' });

  // Mirror EVERY panel-state transition to the optional parent listener (the
  // safety strip, reason line, and questions block render from the same one
  // fetch). Same-reference states no-op upstream, so this can never loop.
  useEffect(() => {
    onStateChange?.(state);
  }, [state, onStateChange]);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    readQuestionnaireDeduped(appointmentId)
      .then((res) => {
        if (cancelled) return;
        // Doctor (clinical) -> ready; a non-clinical shape means this session
        // is not the doctor audience.
        setState(preconsultPanelStateFromRead(res));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({ kind: 'error', text: consultErrorMessage(err).text });
      });
    return () => {
      cancelled = true;
    };
  }, [appointmentId]);

  // HEADLESS: the fetch owner renders nothing (the review card is folded --
  // CONSULT-INTAKE-FOLD-V1). Its state reaches the visible surfaces through
  // `onStateChange`.
  return null;
}
